import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin, BadgeCheck, ExternalLink, ShieldCheck } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PORTAL_URL = "https://www.dalimitglobal.com/jobs";

export const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    axios
      .get(`${API}/jobs`)
      .then((r) => setJobs(r.data))
      .catch((e) => console.error("jobs fetch failed", e));
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(jobs.map((j) => j.category)))],
    [jobs]
  );
  const visible = filter === "All" ? jobs : jobs.filter((j) => j.category === filter);

  return (
    <section id="roles" data-testid="jobs-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-6">
          <Reveal>
            <p className="font-body text-sm tracking-[0.25em] uppercase text-[#ff3b30] mb-6">
              Featured vacancies
            </p>
            <h2 className="font-head uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl xl:text-8xl">
              Live roles
              <br />
              across Europe.
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-2" data-testid="job-filters">
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`filter-${c.toLowerCase()}`}
                  onClick={() => setFilter(c)}
                  className={`font-body text-sm px-4 py-2 border transition-colors duration-300 ${
                    filter === c
                      ? "border-[#ff3b30] text-[#ff3b30]"
                      : "border-[#e4e4e7] text-[#52525b] hover:text-[#0a0a0a]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.05}>
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="portal-banner"
            className="group mb-10 flex items-center justify-between gap-4 border border-[#0a0a0a] bg-[#0a0a0a] hover:bg-[#ff3b30] hover:border-[#ff3b30] px-6 md:px-8 py-5 transition-colors duration-300"
          >
            <p className="font-body text-sm md:text-base text-white">
              Applications are made on the{" "}
              <span className="font-600">DALIMIT GLOBAL Job Portal</span> — Daab Consulting's
              international application portal. One profile, multiple countries.
            </p>
            <span className="inline-flex items-center gap-2 shrink-0 font-head uppercase text-sm tracking-wide text-white">
              Apply online
              <ExternalLink
                size={16}
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"
              />
            </span>
          </a>
        </Reveal>

        <div className="border-t border-[#e4e4e7]">
          <AnimatePresence mode="popLayout">
            {visible.map((job, i) => (
              <motion.div
                key={job.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`job-row-${job.id}`}
                className="group grid grid-cols-12 gap-4 items-center px-2 md:px-6 py-7 md:py-8 border-b border-[#e4e4e7] hover:bg-[#f5f5f4] transition-colors duration-400"
              >
                <div className="col-span-12 md:col-span-3">
                  <h3 className="font-head uppercase tracking-tight text-2xl md:text-3xl group-hover:text-[#ff3b30] transition-colors duration-300">
                    {job.title}
                  </h3>
                  <span className="inline-flex items-center gap-1.5 font-body text-xs text-[#52525b] mt-1">
                    <BadgeCheck size={13} className="text-[#ff3b30] shrink-0" />
                    {job.eligibility}
                  </span>
                </div>
                <div className="col-span-6 md:col-span-2 font-body text-sm text-[#52525b] flex items-center gap-2">
                  <MapPin size={14} className="text-[#a1a1aa]" /> {job.location}
                </div>
                <div className="col-span-6 md:col-span-2 font-body text-sm font-600 text-[#0a0a0a]">
                  {job.salary}
                </div>
                <div className="col-span-12 md:col-span-3 font-body text-xs text-[#52525b]">
                  <span className="text-[#a1a1aa] uppercase tracking-wide">Accommodation: </span>
                  {job.accommodation}
                </div>
                <div className="col-span-12 md:col-span-2 flex md:flex-col md:items-end gap-2 md:gap-1.5 items-center">
                  <a
                    href={PORTAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`apply-btn-${job.id}`}
                    className="group/btn inline-flex items-center gap-2 bg-[#0a0a0a] hover:bg-[#ff3b30] text-white font-head uppercase text-sm tracking-wide px-5 py-3 transition-colors duration-300"
                  >
                    Apply online
                    <ArrowUpRight
                      size={16}
                      className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform duration-300"
                    />
                  </a>
                  <span
                    data-testid={`portal-badge-${job.id}`}
                    className="inline-flex items-center gap-1 font-body text-[11px] text-[#a1a1aa]"
                  >
                    <ShieldCheck size={12} className="text-[#ff3b30] shrink-0" />
                    Official application portal
                  </span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <Reveal delay={0.1}>
          <p className="font-body text-[#a1a1aa] text-sm mt-8">
            Salaries shown are indicative gross monthly unless stated as net. New roles are added
            every week across the EU. To apply, create your profile on the{" "}
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#ff3b30] underline underline-offset-4 hover:text-[#0a0a0a] transition-colors"
            >
              DALIMIT GLOBAL Job Portal
            </a>
            .
          </p>
        </Reveal>
      </div>
    </section>
  );
};
